import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/adminApi";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const Toggle = ({ label, desc, checked, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4 py-4 border-b border-slate-200 last:border-0">
    <div><p className="font-medium text-slate-800">{label}</p><p className="text-xs text-slate-500 mt-0.5">{desc}</p></div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

const AdminSettings = ({ onAuthError }) => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.get("/admin/settings").then((r) => setS(r.data)).catch(onAuthError); }, []);

  const save = async () => {
    setSaving(true);
    try { await adminApi.put("/admin/settings", s); toast.success("Settings saved"); }
    catch (e) { onAuthError(e); toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="flex justify-center py-20 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  return (
    <div className="max-w-2xl" data-testid="admin-settings">
      <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5">
        <Toggle label="Show upgrade CTAs" desc="Display the locked Career Direction / Career Plan upgrade cards to users." checked={s.show_upgrade_ctas} onChange={(v) => setS({ ...s, show_upgrade_ctas: v })} testId="setting-upgrade-ctas" />
        <Toggle label="Maintenance mode" desc="Show a maintenance banner to all users." checked={s.maintenance_mode} onChange={(v) => setS({ ...s, maintenance_mode: v })} testId="setting-maintenance" />
        <div className="pt-4">
          <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Announcement banner</label>
          <Textarea value={s.announcement} onChange={(e) => setS({ ...s, announcement: e.target.value })} placeholder="Shown to all users at the top of the app. Leave empty to hide." className="bg-slate-50 border-slate-300 text-slate-900" data-testid="setting-announcement" />
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5" data-testid="admin-builder-limits">
        <h3 className="font-bold text-slate-900">Resume builder allowances</h3>
        <p className="text-xs text-slate-500 mt-1">How many resumes a member can build from scratch, and how many AI rewrites each built resume gets before hand-editing is the only option.</p>
        <div className="mt-4 space-y-3">
          {["free", "growth", "elite"].map((tier) => (
            <div key={tier} className="grid grid-cols-3 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`builder-limits-${tier}`}>
              <p className="text-sm font-semibold capitalize text-slate-800">{tier}</p>
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500">Built resumes
                <Input type="number" min={0} max={500} value={s.builder?.[tier]?.resumes ?? 0}
                  onChange={(e) => setS({ ...s, builder: { ...s.builder, [tier]: { ...s.builder[tier], resumes: Number(e.target.value) } } })}
                  className="mt-1 bg-white border-slate-200 text-slate-900" data-testid={`builder-limit-resumes-${tier}`} />
              </label>
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500">AI rewrites
                <Input type="number" min={0} max={50} value={s.builder?.[tier]?.regenerations ?? 0}
                  onChange={(e) => setS({ ...s, builder: { ...s.builder, [tier]: { ...s.builder[tier], regenerations: Number(e.target.value) } } })}
                  className="mt-1 bg-white border-slate-200 text-slate-900" data-testid={`builder-limit-regenerations-${tier}`} />
              </label>
            </div>
          ))}
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="mt-4 bg-sky-600 hover:bg-sky-500 text-white" data-testid="admin-settings-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save settings"}
      </Button>
    </div>
  );
};

export default AdminSettings;
